import { forwardRef, useMemo, ChangeEvent } from "react";
import { Stack, Tooltip, IconButton, Typography } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslate } from "@refinedev/core";

import { AudioFilePickerProps } from "./types";

export const AudioFilePicker = forwardRef<HTMLInputElement, AudioFilePickerProps>(
  ({ label, value, onChange, error, helperText, required, disabled }, ref) => {
    const translate = useTranslate();

    const fileName = useMemo(() => {
      if (value instanceof File) return value.name;
      if (typeof value === "string" && value) return value.split("/").pop()?.split("?")[0];
      return null;
    }, [value]);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      onChange?.(file ?? null);
      e.target.value = "";
    };

    return (
      <Stack spacing={0.5}>
        {label && (
          <Typography style={{ fontSize: 14 }}>
            {label}
            {required ? " *" : ""}
          </Typography>
        )}

        <Stack direction="row" spacing={1} alignItems="center" sx={{ height: 45 }}>
          <Tooltip title={translate("buttons.select_file")}>
            <span>
              <IconButton
                component="label"
                disabled={disabled}
                sx={{
                  width: 45,
                  height: 45,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "4px",
                }}
              >
                <UploadFileIcon fontSize="small" />
                <input
                  ref={ref}
                  type="file"
                  accept="audio/*"
                  hidden
                  onChange={handleChange}
                />
              </IconButton>
            </span>
          </Tooltip>

          {fileName && (
            <>
              <Typography noWrap style={{ fontSize: 14, maxWidth: 220 }}>
                {fileName}
              </Typography>
              {!disabled && (
                <IconButton size="small" onClick={() => onChange?.(null)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </>
          )}
        </Stack>

        {error && helperText && (
          <Typography style={{ fontSize: 12, color: "#d32f2f" }}>
            {helperText}
          </Typography>
        )}
      </Stack>
    );
  }
);

AudioFilePicker.displayName = "AudioFilePicker";
